import {
  Box,
  DescriptionList,
  formatNumber,
  Inline,
  Skeleton,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import type { ContractorPayment } from '@omnipresentgroup/types';

const PAYMENT_METHOD = 'Payment method';
const TRANSACTION_ID = 'Transaction ID';
const CONTRACTOR_INVOICES = 'Contractor invoices';
const GRAND_TOTAL = 'Grand total';
const REFERENCE_DESCRIPTION = 'Reference/description';

const TransactionDetailsSkeleton = () => {
  return (
    <Stack gap="16">
      <Stack gap="8">
        <Skeleton height={32} width={200} />
        <Skeleton height={14} />
      </Stack>
      <Stack gap="4">
        <Skeleton height={14} width={200} />
        <Skeleton height={40} />
      </Stack>
      <Stack gap="4">
        <Skeleton height={14} width={200} />
        <Skeleton height={40} />
      </Stack>
      <Stack gap="4">
        <Skeleton height={14} width={200} />
        <Skeleton height={40} />
      </Stack>
      <Stack gap="4">
        <Skeleton height={14} width={200} />
        <Skeleton height={40} />
      </Stack>
    </Stack>
  );
};

type Props = {
  payment: ContractorPayment;
  loading: boolean;
};

export const TransactionDetails = ({ payment, loading }: Props) => {
  const { id, quote, transferDetails } = payment;

  const transactionDetailsList: {
    label: string;
    value: string | number | JSX.Element;
  }[] =
    id && quote
      ? [
          {
            label: PAYMENT_METHOD,
            value: toSentenceCase(quote.paymentType),
          },
          {
            label: TRANSACTION_ID,
            value: id,
          },
          {
            label: CONTRACTOR_INVOICES,
            value: quote.invoiceBreakdown?.length,
          },
          {
            label: GRAND_TOTAL,
            value: formatNumber({
              input: Number(quote.quoteSummary.transferAmount.amount),
              currency: quote.quoteSummary.transferAmount.currency,
            }),
          },
          ...(transferDetails?.wireReference
            ? [
                {
                  label: REFERENCE_DESCRIPTION,
                  value: transferDetails.wireReference,
                },
              ]
            : []),
        ]
      : [];

  const list = transactionDetailsList.map(({ label, value }) => ({
    header: label,
    detail: (
      <Inline justify="space-between" align="center">
        <Box overflow="auto">
          {typeof value === 'string' ? (
            <Typography size="14" as="span">
              {value}
            </Typography>
          ) : (
            value
          )}
        </Box>
      </Inline>
    ),
  }));

  return loading ? (
    <TransactionDetailsSkeleton />
  ) : (
    <DescriptionList list={list} id={`transaction-details-${id}`} />
  );
};
